import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { JamNight } from "@shared/schema";

const formSchema = z.object({
  timeLimit: z.coerce.number().int().min(0, "Time limit must be a positive number").optional().nullable(),
});

type FormData = z.infer<typeof formSchema>;

interface SetTimeLimitModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  jamNight: JamNight;
}

export default function SetTimeLimitModal({ isOpen, onOpenChange, jamNight }: SetTimeLimitModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      timeLimit: jamNight.timeLimit,
    },
  });

  const setTimeLimitMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const payload = { 
        ...jamNight, 
        date: new Date(jamNight.date).toISOString(),
        timeLimit: data.timeLimit || null 
      };
      const response = await fetch(`/api/events/${jamNight.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error('Failed to set time limit');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/events/${jamNight.id}`] });
      toast({
        title: "Success",
        description: "Time limit updated successfully!",
      });
      onOpenChange(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to set time limit",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FormData) => {
    setTimeLimitMutation.mutate(data);
  };

  const removeLimit = () => {
    form.setValue("timeLimit", null);
    setTimeLimitMutation.mutate({ timeLimit: null });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Set Time Limit</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="timeLimit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Time Limit (minutes)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Enter time limit in minutes"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={removeLimit}
                disabled={setTimeLimitMutation.isPending}
              >
                Remove Limit
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-blue-600/80 backdrop-blur-sm text-white hover:bg-blue-700/90 border border-blue-500/30 shadow-lg transition-all duration-300"
                style={{
                  boxShadow: '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
                disabled={setTimeLimitMutation.isPending}
              >
                {setTimeLimitMutation.isPending ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
} 